import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

import { inputClass } from '../design-system/tokens'

export interface SegmentedDateInputProps {
  /** YYYY-MM-DD or empty string while incomplete. */
  value: string
  onChange: (next: string) => void
  /** Optional id for the day segment so a wrapping <Label htmlFor> works. */
  id?: string
}

interface Segments {
  day: string
  month: string
  year: string
}

function parse(iso: string): Segments {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return { day: '', month: '', year: '' }
  }
  const [year, month, day] = iso.split('-')
  return { day: day ?? '', month: month ?? '', year: year ?? '' }
}

function assemble(seg: Segments): string {
  if (seg.day.length === 2 && seg.month.length === 2 && seg.year.length === 4) {
    return `${seg.year}-${seg.month}-${seg.day}`
  }
  return ''
}

/**
 * Three numeric segments (dd / mm / aaaa) that auto-advance focus once each
 * segment is filled and step back to the previous segment on backspace from
 * an empty one. Emits the YYYY-MM-DD ISO string via `onChange` only when all
 * three segments are complete; otherwise emits an empty string so downstream
 * validators see an obvious "incomplete" state.
 *
 * The day input is the focusable target — wrap the component in a
 * <Label htmlFor={id}> and pass that id here so tests and screen readers
 * land on the right element.
 */
export const SegmentedDateInput = forwardRef<HTMLInputElement, SegmentedDateInputProps>(
  function SegmentedDateInput({ value, onChange, id }, externalRef) {
    const dayRef = useRef<HTMLInputElement | null>(null)
    const monthRef = useRef<HTMLInputElement | null>(null)
    const yearRef = useRef<HTMLInputElement | null>(null)
    const segRef = useRef<Segments>(parse(value))

    useImperativeHandle(externalRef, () => dayRef.current as HTMLInputElement)

    // Sync segments to the incoming `value` ONLY when the parent pushes a
    // complete ISO date that differs from what the segments already show.
    // Skipping the sync on partial/empty values is critical: while the user
    // is typing (e.g. just '1' in the day segment) we emit '' upstream, and
    // re-applying that empty value here would clobber the digit they just
    // typed AND wipe the month / year that were carried over from the
    // previous save. The reset / open flows always push a full ISO so this
    // still keeps the input in sync with form.reset() / template fill.
    useEffect(() => {
      if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return
      const parsed = parse(value)
      if (
        parsed.day === segRef.current.day &&
        parsed.month === segRef.current.month &&
        parsed.year === segRef.current.year
      ) {
        return
      }
      segRef.current = parsed
      if (dayRef.current) dayRef.current.value = parsed.day
      if (monthRef.current) monthRef.current.value = parsed.month
      if (yearRef.current) yearRef.current.value = parsed.year
    }, [value])

    const onSegmentChange = (
      next: 'day' | 'month' | 'year',
      raw: string,
      nextFocusRef: React.RefObject<HTMLInputElement | null> | null,
      maxLength: number,
    ): void => {
      const cleaned = raw.replace(/\D/g, '').slice(0, maxLength)
      segRef.current = { ...segRef.current, [next]: cleaned }
      const target =
        next === 'day' ? dayRef : next === 'month' ? monthRef : yearRef
      if (target.current) target.current.value = cleaned
      onChange(assemble(segRef.current))
      if (cleaned.length === maxLength && nextFocusRef?.current) {
        nextFocusRef.current.focus()
        nextFocusRef.current.select()
      }
    }

    const onSegmentKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
      previousRef: React.RefObject<HTMLInputElement | null> | null,
    ): void => {
      const input = event.currentTarget
      if (event.key === 'Backspace' && input.value === '' && previousRef?.current) {
        event.preventDefault()
        previousRef.current.focus()
        const length = previousRef.current.value.length
        previousRef.current.setSelectionRange(length, length)
      }
    }

    const segmentClass = `${inputClass} w-12 px-2 py-1 text-center font-mono`

    return (
      <div className="border-input flex items-center gap-1 rounded-md border bg-transparent px-2 py-1 shadow-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        <input
          ref={dayRef}
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={2}
          placeholder="dd"
          defaultValue={segRef.current.day}
          aria-label="Dia"
          onChange={(e) => onSegmentChange('day', e.target.value, monthRef, 2)}
          onKeyDown={(e) => onSegmentKeyDown(e, null)}
          onFocus={(e) => e.currentTarget.select()}
          className={segmentClass.replace('border ', '').replace('border-input', '')}
        />
        <span className="text-muted-foreground select-none">/</span>
        <input
          ref={monthRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={2}
          placeholder="mm"
          defaultValue={segRef.current.month}
          aria-label="Mês"
          onChange={(e) => onSegmentChange('month', e.target.value, yearRef, 2)}
          onKeyDown={(e) => onSegmentKeyDown(e, dayRef)}
          onFocus={(e) => e.currentTarget.select()}
          className={segmentClass.replace('border ', '').replace('border-input', '')}
        />
        <span className="text-muted-foreground select-none">/</span>
        <input
          ref={yearRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={4}
          placeholder="aaaa"
          defaultValue={segRef.current.year}
          aria-label="Ano"
          onChange={(e) => onSegmentChange('year', e.target.value, null, 4)}
          onKeyDown={(e) => onSegmentKeyDown(e, monthRef)}
          onFocus={(e) => e.currentTarget.select()}
          className={`${segmentClass.replace('border ', '').replace('border-input', '')} w-16`}
        />
      </div>
    )
  },
)
